import { useState } from 'react';
import { KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import * as repo from '../data/votingRepository';
import { useManualVoters, useVotingMutation, votingKeys } from '../hooks/useVoting';

/** Voter list of an election without event: people are entered by hand (name + CI) */
export function VotersPanel({ electionId, canEdit }: { electionId: string; canEdit: boolean }) {
  const { data: voters = [], isLoading } = useManualVoters(electionId, true);
  const [name, setName] = useState('');
  const [ci, setCi] = useState('');
  const keys = [votingKeys.voters(electionId)];

  const add = useVotingMutation(electionId, () => repo.addManualVoter(electionId, name, ci), { extraKeys: keys, success: 'Votante agregado' });
  const toggle = useVotingMutation(electionId, (a: { id: string; enabled: boolean }) => repo.setManualVoterEnabled(a.id, a.enabled), { extraKeys: keys });
  const regen = useVotingMutation(electionId, (id: string) => repo.regeneratePin({ voterId: id }), { extraKeys: keys, success: 'PIN nuevo generado' });
  const remove = useVotingMutation(electionId, (id: string) => repo.removeManualVoter(id), { extraKeys: keys, success: 'Votante quitado' });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Votantes ({voters.length})</CardTitle>
        <CardDescription>
          Esta elección no tiene evento: registra aquí a quienes pueden votar (nombre completo y CI). Cada votante tiene un PIN de 6 dígitos para votar
          desde su teléfono (entrégueselo en persona). Apaga el interruptor para que una persona no pueda votar sin borrarla. El CI no se puede repetir.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit && (
          <form
            className="grid gap-2 sm:grid-cols-[1fr_180px_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(undefined, { onSuccess: () => { setName(''); setCi(''); } });
            }}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre completo" />
            <Input value={ci} onChange={(e) => setCi(e.target.value)} placeholder="CI" />
            <Button type="submit" disabled={add.isPending || name.trim().length < 3 || ci.trim().length < 5}>
              {add.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Plus className="mr-2 size-4" />} Agregar
            </Button>
          </form>
        )}

        {isLoading ? (
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        ) : voters.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay votantes.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {voters.map((v) => (
              <li key={v.id} className="flex items-center gap-3 p-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{v.full_name}</p>
                  <p className="text-xs text-muted-foreground">CI {v.ci} · PIN <span className="font-mono font-semibold text-foreground">{v.pin ?? '—'}</span></p>
                </div>
                {canEdit && (
                  <>
                    <Switch
                      checked={v.is_enabled}
                      aria-label="Habilitado para votar"
                      onCheckedChange={(enabled) => toggle.mutate({ id: v.id, enabled })}
                    />
                    <Button size="icon" variant="ghost" title="Generar un PIN nuevo" onClick={() => regen.mutate(v.id)}>
                      <KeyRound className="size-4" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Quitar votante" onClick={() => remove.mutate(v.id)}>
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
